import type { BusinessType, OpeningHoursSpec, BusinessProfile } from '@synvya/capabilities'
import { ensureMerchantSession } from '@/services/merchantAuth'
import type { RestaurantSigner } from '@/services/serverRegistration'

// ── Types — mirror of server/src/services/profile-builder/types.ts ────

export interface OwnerPhoto {
  name: string
  widthPx: number
  heightPx: number
}

export interface ProfileBuilderCandidate {
  placeId: string
  displayName: string
  name: string
  nip05: string
  businessType: BusinessType
  primaryType: string
  phone: string
  websiteUrl: string
  formattedAddress: string
  street: string
  city: string
  state: string
  zip: string
  country: string
  googleMapsUrl: string
  openingHours: OpeningHoursSpec[]
  cuisine: string[]
  categories: string[]
  ownerPhotos: OwnerPhoto[]
  latitude: number | null
  longitude: number | null
}

export interface GenerateAboutInput {
  businessName: string
  websiteUrl?: string
  googlePlacesData: {
    primaryType: string
    city: string
    state: string
    cuisine?: string[]
    categories?: string[]
    types?: string[]
    serviceFlags?: Record<string, boolean>
  }
}

// ── Helpers ──────────────────────────────────────────────────────────

function getServerBaseUrl(): string {
  const base = import.meta.env.VITE_SERVER_BASE_URL as string | undefined
  if (!base) throw new Error('Missing VITE_SERVER_BASE_URL')
  return base.replace(/\/+$/, '')
}

// ── API ──────────────────────────────────────────────────────────────

export async function searchBusinesses(
  query: string,
): Promise<ProfileBuilderCandidate[]> {
  const url = `${getServerBaseUrl()}/api/profile-builder/search`
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    throw new Error(`Search failed (${res.status}): ${text}`)
  }
  const data = await res.json()
  return data.candidates
}

export async function generateAbout(
  input: GenerateAboutInput,
): Promise<string> {
  const url = `${getServerBaseUrl()}/api/profile-builder/generate-about`
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    throw new Error(`About generation failed (${res.status}): ${text}`)
  }
  const data = await res.json()
  return data.about
}

/** What a geocode call needs to re-establish an expired merchant session. */
export interface GeocodeSession {
  restaurantPubkey: string
  signAsRestaurant: RestaurantSigner
}

/**
 * Coordinates for an address, with the IANA time zone that contains them.
 * `timezone` is null when the server could not determine one (server#311).
 *
 * The endpoint is gated by the merchant session cookie, which lasts 15 minutes
 * and does not slide. With a `session`, a 401 re-authenticates once and
 * retries; without one it is an error, as before. A profile left open past
 * the cookie's life would otherwise save with no geo tags and no time zone
 * correction, and report success (account#254).
 */
export async function geocodeAddress(
  address: string,
  session?: GeocodeSession,
): Promise<{ latitude: number; longitude: number; timezone: string | null }> {
  const url = `${getServerBaseUrl()}/api/profile-builder/geocode`
  const call = () =>
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }),
    })
  let res = await call()
  if (res.status === 401 && session) {
    await ensureMerchantSession(session.restaurantPubkey, session.signAsRestaurant)
    res = await call()
  }
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    throw new Error(`Geocoding failed (${res.status}): ${text}`)
  }
  return res.json()
}

export function getPhotoUrl(photoName: string, maxWidthPx = 800): string {
  return `${getServerBaseUrl()}/api/profile-builder/photo?name=${encodeURIComponent(photoName)}&maxWidthPx=${maxWidthPx}`
}

// ── Candidate → Profile mapping ─────────────────────────────────────

export function candidateToProfile(candidate: ProfileBuilderCandidate): Partial<BusinessProfile> {
  const name = candidate.displayName.replace(/\s+/g, '').toLowerCase()
  return {
    displayName: candidate.displayName,
    name,
    nip05: `${name}@synvya.com`,
    businessType: candidate.businessType,
    phone: candidate.phone || undefined,
    website: candidate.websiteUrl || undefined,
    street: candidate.street || undefined,
    city: candidate.city || undefined,
    state: candidate.state || undefined,
    zip: candidate.zip || undefined,
    country: candidate.country || 'US',
    googleMapsUrl: candidate.googleMapsUrl || undefined,
    googlePlaceId: candidate.placeId || undefined,
    openingHours: candidate.openingHours.length > 0 ? candidate.openingHours : undefined,
    cuisine: candidate.cuisine.length > 0 ? candidate.cuisine.join(', ') : undefined,
    categories: candidate.categories.length > 0 ? candidate.categories : undefined,
    // **No claims are seeded here, and that is the rule rather than a gap.** A
    // claim is the merchant's own statement about their premises, so a Google
    // signal may be staged for confirmation (`[EX-9]`) but is never written into
    // a profile on their behalf (venue-claims.md VA-11). The candidate
    // carries none for the same reason.
  }
}

